import {useEffect, useMemo, useState} from 'react';
import Button from '@/shared/components/Button';
import {Box, Typography} from '@mui/material';
import {Field, Form, Formik} from 'formik';
import Input from '@/shared/components/Inputs/Input';
import * as Yup from 'yup';
import {CustomizationStyle} from '../Customisation/Customization.style';
import {formContentStyle} from '@/components/Organization/Dashboard/CreateNewEvent/EventSetting/FormContent/FormContent.styles';
import {ReactComponent as Instagram} from '@/assets/icons/instagram.svg';
import {ReactComponent as Facebook} from '@/assets/icons/facebook.svg';
import {ReactComponent as YouTube} from '@/assets/icons/youtube.svg';
import {ReactComponent as Twitter} from '@/assets/icons/twiter.svg';
import {ReactComponent as PdfIcon} from '@/assets/icons/pdf-icon.svg';
import {billingHistory} from '../../Profile/Billing/History/BillingHistory.style';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import {
	getAllEmailTemplates,
	getEmailCustomizationTemplateSelector,
	getEmailTemplate,
	getEmailTemplatesSelector,
	getOneEmailTemplateByType,
	organizationCommunicationLoaderSelector,
	updateOneEmailTemplateByType,
} from '@/store/organization-communication';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector, getEventsLoadingSelector} from '@/store/organization-event';
import Loader from '@/shared/components/Loader';
import {position} from '@/utils/constants/communication';
import {CommunicationService} from '@/utils/services/organization-communication.services';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {IEmailTemplate, IEmailTemplateResponse} from '@/utils/types/organization-communication.interface';
import {EMAIL_TEMPLATE_TYPE} from '@/utils/enums';

const validationSchema = Yup.object().shape({
	title: Yup.string().required('Required'),
	text: Yup.string().required('Required'),
});

const InvoiceEmail = () => {
	const dispatch = useAppDispatch();

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const emailTemplates = useAppSelector(getEmailTemplatesSelector);
	const communicationTemplate = useAppSelector(getEmailCustomizationTemplateSelector);
	const communicationTemplateLoading = useAppSelector(organizationCommunicationLoaderSelector);

	const [isLoading, setIsLoading] = useState(false);

	const templateType = EMAIL_TEMPLATE_TYPE.INVOICE_EMAIL;

	const initialValues = useMemo(() => {
		return (
			emailTemplates?.find((template) => template.templateType === templateType) ||
			({
				title: '',
				text: '',
			} as IEmailTemplateResponse)
		);
	}, [currentEvent?.id, templateType, emailTemplates]);

	const handleSubmit = async (values: typeof initialValues) => {
		if (!currentEvent) return;

		setIsLoading(true);
		try {
			const reqBody: IEmailTemplate = {
				eventId: currentEvent?.id,
				title: values.title || '',
				text: values.text || '',
			};

			await dispatch(updateOneEmailTemplateByType({templateType, reqBody})).unwrap();
			toast.success('Saved successfully');
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handleSendEmail = async () => {
		if (!currentEvent) return;
		try {
			await CommunicationService.sendTestEmailTemplate(currentEvent?.id, templateType);
			toast.success('Email sent successfully');
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	useEffect(() => {
		if (!currentEvent) return;

		const getCurrentTemplate = async () => {
			setIsLoading(true);
			try {
				await dispatch(getEmailTemplate(currentEvent?.id)).unwrap();
				await dispatch(getAllEmailTemplates(currentEvent?.id)).unwrap();
				await dispatch(
					getOneEmailTemplateByType({
						eventId: currentEvent?.id as number,
						templateType,
					})
				).unwrap();
			} catch (error) {
				toast.error(getErrorMessage(error));
			} finally {
				setIsLoading(false);
			}
		};

		getCurrentTemplate();
	}, [currentEvent?.id]);

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			enableReinitialize={true}
		>
			{({handleSubmit, values, resetForm}) => (
				<Form onSubmit={handleSubmit}>
					<Box sx={CustomizationStyle.mainWrapper}>
						<Box sx={CustomizationStyle.leftBoxWrapper}>
							<Box>
								<Typography sx={CustomizationStyle.heading}>Email Settings</Typography>

								{isLoading ? (
									<Box sx={{height: 'calc(100vh - 270px)'}}>
										<Loader sx={formContentStyle.loader} size={50} />
									</Box>
								) : (
									<Box sx={CustomizationStyle.inputs}>
										<Field id="title" name="title" label="Title" component={Input} placeholder="Enter title" />
										<Field
											id="text"
											name="text"
											rows={11}
											sx={formContentStyle.description}
											placeholder="Your message"
											label="Message"
											component={Input}
											multiline
										/>
									</Box>
								)}

								<Box sx={CustomizationStyle.buttonsBlock}>
									<Button
										variant="outline"
										title="Cancel"
										onClick={() => {
											resetForm();
										}}
									/>
									<Button
										title="Save changes"
										type="submit"
										disabled={values.text === '' || values.title === '' || isLoading}
									/>
								</Box>
							</Box>
						</Box>

						<Box sx={CustomizationStyle.rightBoxWrapper}>
							<Box>
								<Typography sx={CustomizationStyle.rightHeading}>Template Preview</Typography>

								{communicationTemplateLoading ? (
									<Box sx={{height: 'calc(100vh - 270px)'}}>
										<Loader sx={formContentStyle.loader} size={50} />
									</Box>
								) : (
									<Box
										sx={{
											background: communicationTemplate?.bodyBackground || '#FFFFFF',
											height: ' calc(100vh - 270px)',
											overflowY: 'auto',
											borderRadius: '8px',
										}}
									>
										<Box>
											{communicationTemplate && communicationTemplate?.logo ? (
												<Box
													sx={{
														display: 'flex',
														justifyContent: position[communicationTemplate.logoPosition as keyof typeof position],
														padding: '12px',
														background: communicationTemplate.headerColor,
													}}
												>
													<img
														src={communicationTemplate?.logo}
														alt="Uploaded Logo"
														style={{
															maxWidth: '100%',
															marginBottom: '10px',
															height: `${communicationTemplate.logoHeight}px`,
														}}
													/>
												</Box>
											) : null}
										</Box>
										<Box>
											{communicationTemplate && communicationTemplate?.eventLogo && (
												<Box sx={{display: 'flex'}}>
													<img
														src={communicationTemplate.eventLogo}
														alt="Uploaded Logo"
														style={CustomizationStyle.eventLogoRight}
													/>
												</Box>
											)}
										</Box>

										<Box sx={{mt: '24px'}}>
											<Typography sx={CustomizationStyle.rightTitle}>{values?.title}</Typography>
											<Typography sx={CustomizationStyle.rightMessage}>{values?.text}</Typography>
										</Box>
										<Box sx={CustomizationStyle.pdfContainer}>
											<Box sx={{display: 'flex'}}>
												<Box sx={{...billingHistory.iconWrap}}>
													<PdfIcon />
												</Box>
												<Box sx={{height: '40px'}}>
													<Typography sx={billingHistory.invoiceName}>invoice.pdf</Typography>
													<p style={billingHistory.neutral300}>15 mb</p>
												</Box>
											</Box>

											{/* <ActionsMenu disableRipple content={<> </>} /> */}
										</Box>
										<Box sx={{px: '24px'}}>
											<Button sx={CustomizationStyle.topBtn} title="Make payment" />
										</Box>

										<Box sx={{marginTop: '24px', marginBottom: '24px'}}>
											<Box sx={CustomizationStyle.linkCont}>
												{communicationTemplate?.socialLinks?.[0] &&
													[
														{
															url: communicationTemplate.socialLinks[0].facebookUrl,
															icon: <Facebook />,
														},
														{
															url: communicationTemplate.socialLinks[0].instagramUrl,
															icon: <Instagram />,
														},
														{
															url: communicationTemplate.socialLinks[0].twitterUrl,
															icon: <Twitter />,
														},
														{
															url: communicationTemplate.socialLinks[0].youTubeUrl,
															icon: <YouTube />,
														},
													]
														.filter((item) => Boolean(item.url))
														.map((item, index) => (
															<Box key={index} sx={CustomizationStyle.linkItem}>
																<a href={item.url} target="_blank" rel="noreferrer">
																	{item.icon}
																</a>
															</Box>
														))}
											</Box>
										</Box>
									</Box>
								)}

								<Box sx={CustomizationStyle.buttonsBlock}>
									<Button
										title="Send test email"
										onClick={handleSendEmail}
										disabled={!communicationTemplate || !initialValues?.text || !initialValues?.title}
									/>
								</Box>
							</Box>
						</Box>
					</Box>
				</Form>
			)}
		</Formik>
	);
};

export default InvoiceEmail;
